import { observable, action, computed, toJS } from 'mobx';
import { groupBy, orderBy, cloneDeep, minBy, sortBy } from 'lodash';

import { logError as sentryLogError } from 'sletat-sentry';

import { DEFAULT_IS_ONLINE_BILLING, DEFAULT_UNLINKED_ROOM_ID, MIN_TOURPACKS_FOR_POSTFILTERS, NUM_TOUR_PACKS_TO_DISPLAY } from '../consts';
import { SearchFormStore } from './search-form';
import { hotelPageLogger } from '../../utils/logger';
import {
    createSearcherService, GetToursAsyncServiceEvents, Tour,
    GetToursRequestParams, GetToursAsyncService, ExtendedTour
} from '../services/get-tours';
import { SearchError, SearchStatus } from '../models/search';
import {
    PostFilters,
    AvailablePostFilters,
    TourOperatorFilter,
    MealTypeFilter,
    RoomTypeFilter,
    DepartDateFilter,
    NightsFilter,
    FilterState
} from '../models/post-filters';
import { TourStore as TourModel } from './tour';
import { TourPackStore as TourPackModel } from './tour-pack';
import { calcSearchProgress, createCompositeKeyForTourPack } from '../utils/search';
import { getLinkedRoomTypeName } from '../utils/room-types/linking';
import { parseDateString } from '../utils/parsers';
import { UiStore } from './ui';
import { ReCaptcha } from '../../utils/reCaptcha';
import { FAILED_TOKEN_MESSAGE } from '../../consts/reCaptcha';
import { formatDay, formatDayOfWeek, shortFormatMonth } from '../utils/format';
import { CurrencyId } from '../models/actualization';
import { normalize } from '../utils/room-types/formating';
import { InitialStateStore } from './initial-state';
import { uniteDupsEntries } from '../utils/room-types/grouping';
import { DEFAULT_DEPART_CITY } from '../../consts/default-geo-city';

export class ToursStore {

    @observable
    searchStatus = SearchStatus.Initial;

    @observable
    searchProgress = 0;

    @observable
    selectedTourId: string | null = null;

    @observable
    numTourPacksToDisplay: number = NUM_TOUR_PACKS_TO_DISPLAY;

    @observable
    allPostFilters: PostFilters = {
        tourOperators: [],
        availableForOnlineBillingOperators: [],
        mealTypes: [],
        roomTypes: [],
        departDates: [],
        nights: []
    };

    @observable
    isOnlineBilling = DEFAULT_IS_ONLINE_BILLING;

    @observable
    shouldShowPostFilters = false;

    searchError: SearchError = {
        type: 0,
        message: ''
    };

    @observable
    private tours: TourModel[] = [];

    private searcher: GetToursAsyncService | null = null;

    constructor(
        private searchFormStore: SearchFormStore,
        private uiStore: UiStore,
        private initialStateStore: InitialStateStore) { }

    @computed
    get isSomeFiltersChecked(): boolean {
        return this.availablePostFilters.departDates.some((f) => f.checked) ||
            this.availablePostFilters.mealTypes.some((f) => f.checked) ||
            this.availablePostFilters.nights.some((f) => f.checked) ||
            this.availablePostFilters.roomTypes.some((f) => f.checked) ||
            this.availablePostFilters.tourOperators.some((f) => f.checked);
    }

    @computed
    get isToursNotFound(): boolean {
        return (this.searchStatus === SearchStatus.Success) && !this.tours.length;
    }

    @computed
    get isSearchFailed(): boolean {
        return this.searchStatus === SearchStatus.Failed;
    }

    @computed
    get availablePostFilters(): AvailablePostFilters {
        const filters = this.allPostFilters;

        const checkedOperators = filters.tourOperators.filter(f => f.checked);
        const checkedMealTypes = filters.mealTypes.filter(f => f.checked);
        const checkedRoomTypes = filters.roomTypes.filter(f => f.checked);
        const checkedDepartDates = filters.departDates.filter(f => f.checked);
        const checkedNights = filters.nights.filter(f => f.checked);

        const fToursWithoutOperators = this.applyPostFilters(this.tours, [], checkedRoomTypes, checkedMealTypes, checkedDepartDates, checkedNights);
        const fToursWithoutMeals = this.applyPostFilters(this.tours, checkedOperators, checkedRoomTypes, [], checkedDepartDates, checkedNights);
        const fToursWithoutRooms = this.applyPostFilters(this.tours, checkedOperators, [], checkedMealTypes, checkedDepartDates, checkedNights);
        const fToursWithoutDepDates = this.applyPostFilters(this.tours, checkedOperators, checkedRoomTypes, checkedMealTypes, [], checkedNights);
        const fToursWithoutNights = this.applyPostFilters(this.tours, checkedOperators, checkedRoomTypes, checkedMealTypes, checkedDepartDates, []);

        const tourOperatorsFilterState = (filter: TourOperatorFilter): FilterState<TourOperatorFilter> => {
            const isAvailable = fToursWithoutOperators.some(t => t.operatorId === filter.id);

        const isAvailableForOnlineBilling = fToursWithoutOperators.some(t => t.operatorId === filter.id
                        && t.isAvailableForOnlineBilling(this.searchFormStore.availableOperatorsIdsForOnlineBillingBySelectedCountry));

            const tours = isAvailable ? fToursWithoutOperators : this.tours;
            const tour = minBy(tours.filter(tour => filter.id === tour.operatorId), t => t.actualPrice);
            const minPrice = tour ? tour.actualPrice : 0;

            return {
                available: isAvailable,
                checked: filter.checked,
                minPrice,
                availableForOnlineBilling: isAvailableForOnlineBilling,
            };
        };

        const mealTypesFilterState = (filter: MealTypeFilter): FilterState<MealTypeFilter> => {
            const isAvailable = fToursWithoutMeals.some(t => t.mealCode === filter.code);

            const tours = isAvailable ? fToursWithoutMeals : this.tours;
            const tour = minBy(tours.filter(tour => filter.code === tour.mealCode), t => t.actualPrice);
            const minPrice = tour ? tour.actualPrice : 0;

            return {
                available: isAvailable,
                checked: filter.checked,
                minPrice: minPrice
            };
        };

        const roomTypesFilterState = (filter: RoomTypeFilter): FilterState<RoomTypeFilter> => {
            const isAvailable = fToursWithoutRooms.some(t => t.roomType === filter.name);

            const tours = isAvailable ? fToursWithoutRooms : this.tours;
            const tour = minBy(tours.filter(tour => filter.name === tour.roomType), t => t.actualPrice);
            const minPrice = tour ? tour.actualPrice : 0;

            return {
                available: isAvailable,
                checked: filter.checked,
                minPrice: minPrice
            };
        };

        const departDatesFilterState = (filter: DepartDateFilter): FilterState<DepartDateFilter>  => {
            const isAvailable = fToursWithoutDepDates.some(t => t.departDateString === filter.date);
            const tours = isAvailable ? fToursWithoutDepDates : this.tours;
            const tour = minBy(tours.filter(tour => filter.date === tour.departDateString), t => t.actualPrice);
            const minPricePerDay = tour ? Math.ceil(tour.actualPrice / tour.nights) : 0;
            const minPrice = tour ? tour.actualPrice : 0;

            return {
                available: isAvailable,
                checked: filter.checked,
                minPrice: minPrice,
                minPricePerDay: minPricePerDay,
            };
        };

        const nightsFilterState = (filter: NightsFilter): FilterState<NightsFilter> => {
            const isAvailable = fToursWithoutNights.some(t => t.nights === filter.count);

            const tours = isAvailable ? fToursWithoutNights : this.tours;
            const tour = minBy(tours.filter(tour => filter.count === tour.nights), t => t.actualPrice);
            const minPrice = tour ? tour.actualPrice : 0;

            return {
                available: isAvailable,
                checked: filter.checked,
                minPrice: minPrice
            };
        };

        return {
            tourOperators: orderBy(filters.tourOperators, ['name'])
                .map(filter => ({
                    ...filter,
                    ...tourOperatorsFilterState(filter)
                })),
            mealTypes: filters.mealTypes
                .map(filter => ({
                    ...filter,
                    ...mealTypesFilterState(filter)
                })),
            roomTypes: filters.roomTypes
                .map(filter => ({
                    ...filter,
                    ...roomTypesFilterState(filter)
                })),
            departDates: filters.departDates
                .map(filter => ({
                    ...filter,
                    ...departDatesFilterState(filter)
                })),
            nights: filters.nights
                .map(filter => ({
                    ...filter,
                    ...nightsFilterState(filter)
                }))
        };
    }

    @computed
    get toursPacks(): TourPackModel[] {
        const filteredTours = this.applyAllPostFilters(this.tours);

        const filteredToursCount = this.tours.length - filteredTours.length;
        if (filteredToursCount) {
            hotelPageLogger.info(`${filteredToursCount} tours were filtered out!`);
        }

        return this.groupToursIntoPacks(filteredTours);
    }

    @computed
    get toursCurrencyId(): CurrencyId {
        if (this.tours.length === 0) {
            return CurrencyId.RUB;
        }

        return this.tours[0].selectedCurrencyId;
    }

    // SLT-2982 for adaptive only
    @computed
    get totalAvailableToursPacks(): TourPackModel[] {
        return this.groupToursIntoPacks(this.tours)
            .filter(tp => this.isTourPackAvailable(tp));
    }

    // https://agile.sletat.ru/jiraurl/browse/SLT-2643?focusedCommentId=203247&page=com.atlassian.jira.plugin.system.issuetabpanels:comment-tabpanel#comment-203247
    @computed
    get availableToursPacks(): TourPackModel[] {
        return this.toursPacks.filter(tp => this.isTourPackAvailable(tp));
    }

    @computed
    get selectedTourPack(): TourPackModel | null {
        if (!this.selectedTourId) {
            return null;
        }
        return this.toursPacks
            .filter(pack => !!pack.similarTours.filter(t => t.tourId === this.selectedTourId)[0])[0] || null;
    }

    @computed
    get selectedTour(): TourModel | null {
        const selectedTourPack = this.selectedTourPack;
        if (!selectedTourPack) {
            return null;
        }

        return selectedTourPack.similarTours.filter(t => t.tourId === this.selectedTourId)[0] || null;
    }

    async selectTourPack(tourPackId: string) {
        const tourPack = this.toursPacks
            .filter(tourPack => tourPack.id === tourPackId)[0];

        if (!tourPack) {
            return;
        }

        this.uiStore.notification.hide();
        this.setSelectedTourId(tourPack.similarTours[0].tourId);
        // return it if actualization is needed
        // await tourPack.tryActualizePack();

        // SLT-2825 делаем перевыбор первого доступного тура после актуализации
        if (tourPack.isTourPackActualizedCompletely && !tourPack.isTourPackSold) {
            this.setSelectedTourId(tourPack.availableTours[0].tourId);
        }
    }

    async loadTours(): Promise<void> {
        const getToursParams = this.searchFormStore.searchRequestParams;
        if (!this.searchFormStore.isTicketsIncluded)
            this.searchFormStore.changeDepartureCityId(DEFAULT_DEPART_CITY.id);

        hotelPageLogger.debug('starting search with params: ', getToursParams);
        this.setSearchProgress(0);
        this.setSearchStatus(SearchStatus.InProcess);
        this.searcher = await this.initSearch(getToursParams);
    }

    async pickFirstPackIfNeeded() {
        const toursPacks = this.toursPacks;

        if (!toursPacks.length) {
            return;
        }

        await this.selectTourPack(toursPacks[0].id);

        const isTourPackSold = this.selectedTourPack && this.selectedTourPack.isTourPackSold;
        const isTourPackNotActualized = this.selectedTourPack &&
            (this.selectedTourPack.isTourPackActualizationFailed || this.selectedTourPack.isFirstTourActualizationFailed);

        if (isTourPackSold) {
            this.uiStore.notification.showVerifiedTourSold();
        } else if (isTourPackNotActualized) {
            this.uiStore.notification.showTourNotVerified();
        }
    }

    stopSearch(): void {
        if (this.searcher) {
            this.searcher.destroy();
            this.searcher = null;
        }
        this.toursPacks.forEach(tourPack => tourPack.stopActualization());
    }

    selectTour(tourId: string) {
        this.setSelectedTourId(tourId);
    }

    @action
    clearFilters(): void {
        this.selectAllTourOperatorFilter();
        this.selectAllDepartureDateFilter();
        this.selectAllMealTypeFilter();
        this.selectAllRoomTypeFilter();
    }

    @action
    setIsOnlineBilling(v: boolean): void {
        this.isOnlineBilling = v;
    }

    @action
    selectAllTourOperatorFilter(): void {
        this.allPostFilters.tourOperators.forEach(t => {
            if (t.checked) {
                t.checked = false;
            }
        });
    }

    @action
    toggleTourOperatorFilter(TOId?: number): void {
        const foundTOFilter = this.allPostFilters.tourOperators
            .filter(f => f.id === TOId)[0];

        if (!foundTOFilter) {
            hotelPageLogger.warn(`touroperator with id "${TOId}" not found!`);
            return;
        }

        foundTOFilter.checked = !foundTOFilter.checked;
    }

    @computed
    get onlineBillingOperatorsMinPrice(): number {
        const operators = this.availablePostFilters.tourOperators;
        if (!operators.length) return 0;

        return operators.reduce((minPrice, operator) => {
            if (operator.availableForOnlineBilling && operator.minPrice < minPrice) {
                minPrice = operator.minPrice;
            }
            return minPrice;
        }, operators[0].minPrice);
    }

    @action
    selectAllDepartureDateFilter(): void {
        this.allPostFilters.departDates.forEach(t => {
            if (t.checked) {
                t.checked = false;
            }
        });
    }

    @action
    toggleDepartureDateFilter(date: string): void {
        const foundDepartDate = this.allPostFilters.departDates
            .filter(f => f.date === date)[0];

        if (!foundDepartDate) {
            hotelPageLogger.warn(`departDate "${date}" not found!`);
            return;
        }

        foundDepartDate.checked = !foundDepartDate.checked;
    }

    @action
    selectAllMealTypeFilter(): void {
        this.allPostFilters.mealTypes.forEach(t => {
            if (t.checked) {
                t.checked = false;
            }
        });
    }

    @action
    toggleMealTypeFilter(mealCode?: string): void {
        const foundMealTypeFilter = this.allPostFilters.mealTypes
            .filter(f => f.code === mealCode)[0];

        if (!foundMealTypeFilter) {
            hotelPageLogger.warn(`"${mealCode}" meal type not found!`);
            return;
        }

        foundMealTypeFilter.checked = !foundMealTypeFilter.checked;
    }

    @action
    selectAllRoomTypeFilter(): void {
        this.allPostFilters.roomTypes.forEach(t => {
            if (t.checked) {
                t.checked = false;
            }
        });
    }

    @action
    toggleRoomTypeFilter(roomType?: string): void {
        const foundRoomTypeFilter = this.allPostFilters.roomTypes
            .filter(f => f.name === roomType)[0];

        if (!foundRoomTypeFilter) {
            hotelPageLogger.warn(`"${roomType}" room type not found!`);
            return;
        }

        foundRoomTypeFilter.checked = !foundRoomTypeFilter.checked;
    }

    @action
    selectAllNightsFilter(): void {
        this.allPostFilters.nights.forEach(t => {
            if (t.checked) {
                t.checked = false;
            }
        });
    }

    @action resetAllFilters(): void {
        this.selectAllMealTypeFilter();
        this.selectAllDepartureDateFilter();
        this.selectAllNightsFilter();
        this.selectAllRoomTypeFilter();
        this.selectAllTourOperatorFilter();
    }

    @action
    toggleNightsFilter(nights: number): void {
        const foundFilter = this.allPostFilters.nights
            .filter(f => f.count === nights)[0];

        if (!foundFilter) {
            hotelPageLogger.warn(`"${nights}" nights not found!`);
            return;
        }

        foundFilter.checked = !foundFilter.checked;
    }

    @action
    showMoreTourPacks(): void {
        const numTourPacksToDisplay = this.numTourPacksToDisplay + NUM_TOUR_PACKS_TO_DISPLAY;
        this.numTourPacksToDisplay = (numTourPacksToDisplay > this.toursPacks.length)
            ? this.toursPacks.length
            : numTourPacksToDisplay;
    }

    @action
    resetNumVisibleTourPacks(): void {
        this.numTourPacksToDisplay = NUM_TOUR_PACKS_TO_DISPLAY;
    }

    @action
    setSsrInitialStateStore(store: InitialStateStore): void {
        this.initialStateStore = store;
    }

    private isTourPackAvailable(tourPack: TourPackModel): boolean {
        return !(tourPack.isTourPackActualizedCompletely && tourPack.isTourPackSold);
    }

    private createTourModels(tours: Tour[]): TourModel[] {
        const tourModels = tours.map(tour => {
            const roomTypeName = tour.hotel.roomType ?? tour.hotel.roomTypeOriginal;
            const extendedTour = cloneDeep(tour) as ExtendedTour;
            const isLinkedRoom = tour.hotel.systemRoomTypeId !== DEFAULT_UNLINKED_ROOM_ID;
            extendedTour.linkedRoomTypeName = isLinkedRoom ? tour.hotel.roomType : normalize(getLinkedRoomTypeName(roomTypeName) || roomTypeName);
            return new TourModel(extendedTour, this.initialStateStore.pageModel?.mealsTypes);
        });
        // SLT-3333
        // reportUnlinkedRoomTypes(tours);
        // reportUnlinkedMealTypes(tours);
        return tourModels;
    }

    private async getReCaptchaToken(): Promise<string> {
        let token = '';
        try {
            token = await ReCaptcha.execute('hotel_page');
        } catch (err) {
            // @ts-expect-error
            if (err.message === FAILED_TOKEN_MESSAGE) {
                // @ts-expect-error
                sentryLogError(err);
            }
            console.error(err);
        }

        return token;
    }

    private async initSearch(params: GetToursRequestParams): Promise<GetToursAsyncService> {
        const failedHandler = (err: any) => {
            this.searchError = {
                type: err ? err.type : 0,
                message: err ? err.message : ''
            };
            this.setSearchStatus(SearchStatus.Failed);
            hotelPageLogger.warn('search failed: ', err);
        };

        this.searchError = { type: 0, message: '' };

        const searcher = createSearcherService(params, await this.getReCaptchaToken(), 3000);

        if (this.searchFormStore.isSearchParamsModified) {
            searcher.once(GetToursAsyncServiceEvents.RequestId, () => {
                searcher.getRequestId().then(requestId => {
                    this.searchFormStore.updateURLRequestId(requestId);
                    this.searchFormStore.setIsSearchParamsModified(false);
                });
            });
        }

        searcher.on(GetToursAsyncServiceEvents.Progress, async () => {
            const state = await searcher.getLoadState();
            hotelPageLogger.debug('search progress with state ', state);
            this.setSearchProgress(calcSearchProgress(state));
        });

        searcher.once(GetToursAsyncServiceEvents.AvailableResultsIfTimeout, async () => {
            const response = await searcher.getTours();
            const tourModels = this.createTourModels(response.tours || []);
            const tourPacks = this.groupToursIntoPacks(tourModels);

            hotelPageLogger.info('search\'s first results have received or just a timeout has reached:',
                toJS(tourModels), toJS(tourPacks));

            this.setTours(tourModels);
            this.setAvailablePostFilters(tourModels);
            this.setVisibilityPostFilters(tourPacks);

            this.pickFirstPackIfNeeded();
        });

        searcher.once(GetToursAsyncServiceEvents.Final, async () => {
            const response = await searcher.getTours();
            const tourModels = this.createTourModels(response.tours || []);
            const tourPacks = this.groupToursIntoPacks(tourModels);

            hotelPageLogger.debug('search completed! tours and tourpacks:', toJS(tourModels), toJS(tourPacks));
            this.setTours(tourModels);
            this.setAvailablePostFilters(tourModels);
            this.setVisibilityPostFilters(tourPacks);
            this.setSearchStatus(SearchStatus.Success);

            this.pickFirstPackIfNeeded();
        });

        searcher.once(GetToursAsyncServiceEvents.GetToursError, failedHandler);
        searcher.once(GetToursAsyncServiceEvents.GetLoadStateError, failedHandler);

        return searcher;
    }

    private groupToursIntoPacks(tours: TourModel[]): TourPackModel[] {
        const packs = groupBy(tours, t => createCompositeKeyForTourPack(t));
        return Object.keys(packs)
            .map(key => new TourPackModel(packs[key]));
    }

    private filteredOperatorsByOnlineBillingFilter(operators: TourOperatorFilter[]): TourOperatorFilter[] {
        if (operators.length) return operators;
        return this.isOnlineBilling ? this.allPostFilters.availableForOnlineBillingOperators ?? [] : [];
    }

    private applyPostFilters(tours: TourModel[], operators: TourOperatorFilter[], roomTypes: RoomTypeFilter[],
        meals: MealTypeFilter[], departDates: DepartDateFilter[], nights: NightsFilter[]): TourModel[] {

        const operatorsByOnlineBillingFilter = this.filteredOperatorsByOnlineBillingFilter(operators);

        return tours.filter(t =>
            (operatorsByOnlineBillingFilter.length === 0 || operatorsByOnlineBillingFilter.some(to => t.operatorId === to.id)) &&
            (roomTypes.length === 0 || roomTypes.some(room => t.roomType === room.name)) &&
            (meals.length === 0 || meals.some(meal => t.mealCode === meal.code)) &&
            (departDates.length === 0 || departDates.some(value => t.departDateString === value.date)) &&
            (nights.length === 0 || nights.some(value => t.nights === value.count)));
    }

    private applyAllPostFilters(tours: TourModel[]): TourModel[] {
        const checkedTO = this.allPostFilters.tourOperators.filter(f => f.checked);
        const checkedRoomTypes = this.allPostFilters.roomTypes.filter(f => f.checked);
        const checkedMeals = this.allPostFilters.mealTypes.filter(f => f.checked);
        const checkedDepartDates = this.allPostFilters.departDates.filter(f => f.checked);
        const checkedNights = this.allPostFilters.nights.filter(f => f.checked);

        return this.applyPostFilters(tours, checkedTO, checkedRoomTypes, checkedMeals, checkedDepartDates, checkedNights);
    }

    @action
    private setAvailablePostFilters(tours: TourModel[]): void {
        const separator = '(.)(.)';

        const tourOperatorGroups = groupBy(tours, t => `${t.operatorId}${separator}${t.operatorName}`);

        let roomTypeGroups = groupBy(tours, t => t.roomType);
        roomTypeGroups = uniteDupsEntries(roomTypeGroups);

        const mealGroups = groupBy(tours, t => `${t.mealCode}${separator}${t.mealName}`);
        const departDatesGroups = groupBy(tours, t => t.departDateString);
        const nightsGroups = groupBy(tours, t => t.nights);

        const postFilters = {
            tourOperators: Object.keys(tourOperatorGroups)
                .map(key => {
                    const [id, name] = key.split(separator);
                    return ({
                        id: Number(id),
                        name,
                        checked: !!this.searchFormStore.searchParamsFromHash.operatorIds?.length
                                ? this.searchFormStore.searchParamsFromHash.operatorIds.includes(id)
                                : false,
                        minPrice: sortBy(tourOperatorGroups[key], t => t.actualPrice)[0].actualPrice,
                        availableForOnlineBilling: this.searchFormStore.availableOperatorsIdsForOnlineBillingBySelectedCountry.includes(Number(id))
                } as unknown as TourOperatorFilter);
            }),

            roomTypes: Object.keys(roomTypeGroups)
                .map(key =>
                    ({
                    name: key,
                    checked: this.searchFormStore.searchParamsFromHash.roomType ? this.searchFormStore.searchParamsFromHash.roomType === key : false,
                    minPrice: sortBy(roomTypeGroups[key], t => t.actualPrice)[0].actualPrice
                } as RoomTypeFilter)),

            mealTypes: Object.keys(mealGroups)
                .map(key => ({
                    code: key.split(separator)[0],
                    name: key.split(separator)[1],
                    checked: this.searchFormStore.selectedMealsTypes
                    ? this.searchFormStore.selectedMealsTypes.includes(
                            key.split(separator)[0]
                        )
                    : false,
                    minPrice: sortBy(mealGroups[key], t => t.actualPrice)[0].actualPrice
                } as MealTypeFilter)),

            departDates: orderBy(Object.keys(departDatesGroups), date => parseDateString(date))
                .map(key => {
                    const tours = departDatesGroups[key];
                    const tourWithMinPrice = sortBy(tours, t => t.actualPrice)[0];
                    const weekDay = tourWithMinPrice.departDate ? formatDayOfWeek(tourWithMinPrice.departDate) : '';
                    const day = weekDay.toLowerCase();
                    const isWeekend = (day === 'сб' || day === 'вс');
                    const minPricePerDay = Math.ceil(tourWithMinPrice.actualPrice / tourWithMinPrice.nights);

                    return {
                        date: key,
                        day: tourWithMinPrice.departDate ? formatDay(tourWithMinPrice.departDate) : '',
                        month: tourWithMinPrice.departDate ? shortFormatMonth(tourWithMinPrice.departDate) : '',
                        weekDay,
                        isWeekend,
                        checked: false,
                        minPrice: tourWithMinPrice.actualPrice,
                        minPricePerDay,
                    } as DepartDateFilter;
                }),

            nights: orderBy(Object.keys(nightsGroups).map(key => parseInt(key, 10)))
                .map(key => ({
                    count: key,
                    checked: false,
                    minPrice: sortBy(nightsGroups[key], t => t.actualPrice)[0].actualPrice
                } as NightsFilter))
        } as PostFilters;

        this.setIsOnlineBilling(this.searchFormStore.searchParamsFromHash.onlineBilling ?? DEFAULT_IS_ONLINE_BILLING);

        postFilters.availableForOnlineBillingOperators = postFilters.tourOperators.filter(operator => operator.availableForOnlineBilling);
        hotelPageLogger.debug(`all post-filters: `, postFilters);

        this.allPostFilters = postFilters;
        this.checkedFiltersByTO();
    }

    @action
    private setVisibilityPostFilters(tourPacks: TourPackModel[]): void {
        this.shouldShowPostFilters = tourPacks.length > MIN_TOURPACKS_FOR_POSTFILTERS;
    }

    // SLT-7517
    @action
    private checkedFiltersByTO(): void {
        const selectedSources = this.searchFormStore.searchRequestParams.selectedSources;
        if (selectedSources?.length)
            this.availablePostFilters.tourOperators.forEach((t) => selectedSources.includes(t.id) &&
            this.toggleTourOperatorFilter(t.id));
    }

    @action
    private setSearchProgress(val: number): void {
        this.searchProgress = val;
    }

    @action
    private setTours(tours: TourModel[]): void {
        this.tours = tours;
    }

    @action
    private setSearchStatus(status: SearchStatus): void {
        this.searchStatus = status;
    }

    @action
    private setSelectedTourId(tourId: string): void {
        this.selectedTourId = tourId;
    }
}
